import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import * as api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useAction, useData } from "@/hooks/useData";
import { Button, DataTable, Loader, Segmented, StatusBadge, TableCard } from "@/components/ui";
import { dateLabel } from "@/lib/format";

const TABS = [
  { value: "all", label: "All" },
  { value: "Pending", label: "Pending", tone: "orange" },
  { value: "Approved", label: "Approved", tone: "green" },
  { value: "Rejected", label: "Rejected", tone: "red" },
  { value: "Cancelled", label: "Cancelled", tone: "slate" },
];

export default function LeaveHistory() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("all");
  const { data: leaves, reload } = useData(() => api.leaves.list(user), [user]);
  const { run, pending } = useAction();

  const cancel = async (id) => {
    const { ok } = await run(() => api.leaves.cancel(id), "Leave request cancelled");
    if (ok) reload();
  };

  if (!leaves) return <Loader />;
  const rows = tab === "all" ? leaves : leaves.filter((l) => l.status === tab);
  const count = (v) => (v === "all" ? leaves.length : leaves.filter((l) => l.status === v).length);

  const columns = [
    { key: "leave_type", header: "Type", render: (l) => <span className="text-xs font-bold text-brand-blue">{l.leave_type}</span> },
    { key: "dates", header: "Dates", render: (l) => <span className="text-xs">{dateLabel(l.from_date)}{l.to_date !== l.from_date && ` → ${dateLabel(l.to_date)}`}</span> },
    { key: "days", header: "Days", render: (l) => <span className="text-xs font-extrabold text-brand-blue">{l.days}</span> },
    { key: "reason", header: "Reason", hide: "lg", className: "max-w-xs", render: (l) => <p className="truncate text-xs text-slate-500">{l.reason}</p> },
    { key: "status", header: "Status", render: (l) => (
      <div>
        <StatusBadge status={l.status} />
        {l.rejection_reason && <p className="text-[11px] text-rose-600 mt-1 max-w-[180px] truncate" title={l.rejection_reason}>{l.rejection_reason}</p>}
      </div>
    ) },
    { key: "action", header: "", align: "right", render: (l) => l.status === "Pending" && (
      <Button size="xs" variant="danger" disabled={pending} onClick={() => cancel(l.id)}>Cancel</Button>
    ) },
  ];

  return (
    <>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <Segmented value={tab} onChange={setTab} options={TABS.map((t) => ({ ...t, count: count(t.value) }))} />
        <Button variant="primary" icon={<Plus size={14} />} onClick={() => navigate("/leave/apply")}>Apply leave</Button>
      </div>
      <TableCard heading={`${rows.length} request${rows.length === 1 ? "" : "s"}`}>
        <DataTable columns={columns} rows={rows} empty={{ title: "No leave requests", message: "Requests you submit will appear here." }} />
      </TableCard>
    </>
  );
}
